import { Loader } from '@googlemaps/js-api-loader'
import { RefObject } from 'react'
import { FieldValues, Path, UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface useGooglePlacesProps<T extends FieldValues> {
  formContext: UseFormReturn<T>
  transformObject: { [K in keyof SimpleAddress]?: Path<T> }
}

interface IUseGooglePlaces {
  initGooglePlaces: (searchInput: RefObject<HTMLInputElement>) => void
}

export interface SimpleAddress {
  street: string
  number: string
  floorBlockStair: string
  postalCode: string
  city: string
  province: string
  country: string
  latitude: number
  longitude: number
}

export interface TransformObject<T> {
  street?: keyof T
  number?: keyof T
  floorBlockStair?: keyof T
  postalCode?: keyof T
  city?: keyof T
  province?: keyof T
  country?: keyof T
  latitude?: keyof T
  longitude?: keyof T
}

export const useGooglePlaces = <T extends FieldValues>({
  formContext,
  transformObject,
}: useGooglePlacesProps<T>): IUseGooglePlaces => {
  const { i18n } = useTranslation()

  const initGooglePlaces = async (
    searchInput: RefObject<HTMLInputElement>,
  ): Promise<void> => {
    await initPlacesLibrary()
    await performAutocomplete(searchInput)
  }

  const initPlacesLibrary = (): Promise<google.maps.PlacesLibrary> => {
    const loader = new Loader({
      apiKey: import.meta.env.VITE_GOOGLE_KEY,
      language: i18n.language,
      region: 'ES',
      libraries: ['marker'],
    })

    return loader.importLibrary('places')
  }

  const performAutocomplete = async (
    searchInput: RefObject<HTMLInputElement>,
  ): Promise<void> => {
    if (searchInput == null || searchInput.current == null) return

    const autocomplete = new window.google.maps.places.Autocomplete(searchInput?.current)
    autocomplete.setFields(['address_component', 'geometry'])
    autocomplete.addListener('place_changed', () =>
      onChangeAddress(autocomplete, searchInput),
    )
  }

  const onChangeAddress = (
    autocomplete: google.maps.places.Autocomplete,
    searchInput: RefObject<HTMLInputElement>,
  ): void => {
    const place = autocomplete.getPlace()
    const simpleAddress = googlePlaceDestructure(place)

    const { setValue, trigger, getValues } = formContext

    Object.entries(simpleAddress).forEach(([key, value]) => {
      const transformKey = key as keyof SimpleAddress
      const formFieldName = transformObject[transformKey]
      if (formFieldName) {
        setValue(formFieldName, value)
        trigger(formFieldName)
      }
    })

    if (searchInput.current && transformObject['street']) {
      searchInput.current.value = getValues(transformObject['street'])
      trigger(transformObject['street'])
    }
  }

  const googlePlaceDestructure = (
    place: google.maps.places.PlaceResult,
  ): SimpleAddress => {
    const address = place.address_components

    let street = address?.find(address => address.types.includes('route'))?.long_name
    let number = address?.find(address =>
      address.types.includes('street_number'),
    )?.long_name

    if (!street) {
      street = address?.find(address => address.types.includes('premise'))?.long_name
    }

    if (!number) number = 's/n'

    const floorBlockStair = address?.find(address =>
      address.types.includes('subpremise'),
    )?.long_name
    const postalCode = address?.find(address =>
      address.types.includes('postal_code'),
    )?.long_name
    const city = address?.find(address => address.types.includes('locality'))?.long_name
    const province = address?.find(address =>
      address.types.includes('administrative_area_level_2'),
    )?.long_name
    const country = address?.find(address => address.types.includes('country'))?.long_name
    const latitude = place.geometry?.location?.lat() || 0
    const longitude = place.geometry?.location?.lng() || 0

    return {
      street: street || '',
      number: number || '',
      floorBlockStair: floorBlockStair || '',
      postalCode: postalCode || '',
      province: province || '',
      city: city || '',
      country: country || '',
      latitude: latitude || 0,
      longitude: longitude || 0,
    }
  }

  return {
    initGooglePlaces,
  }
}
